import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import API_BASE_URL from "../../config";
import ComplianceBrief from "./ComplianceBrief";
import { scorePct, statusBadgeClass, statusLabel, statusText, statusTone } from "./utils";

const MD_PROPS = {
  remarkPlugins: [remarkGfm, remarkMath],
  rehypePlugins: [[rehypeKatex, { throwOnError: false, strict: false, output: "html" }]],
};

export default function ApprovalResultRow({ result, assets, fetchAssets, database }) {
  const [expanded, setExpanded] = useState(false);
  const lv = result.lv_item;
  const specs = lv.specs || {};
  const label = statusLabel(result);
  const tone = statusTone(label) || "neutral";

  const toggle = () => {
    setExpanded((e) => {
      const next = !e;
      if (next && result.z_document_id && fetchAssets) fetchAssets(result.z_document_id);
      return next;
    });
  };

  const docAssets = result.z_document_id && assets ? assets[result.z_document_id] : null;

  return (
    <>
      <tr className={`aa-row aa-row--${tone}`} onClick={toggle}>
        <td className="aa-pos">{lv.position || "—"}</td>
        <td className="aa-title">{lv.title || "—"}</td>
        <td className="aa-specs">
          {specs.Wanddicke && <div><span className="aa-spec-key">Wanddicke:</span> {specs.Wanddicke}</div>}
          {specs.Rohdichteklasse && <div><span className="aa-spec-key">RDK:</span> {specs.Rohdichteklasse}</div>}
          {specs.Druckfestigkeitsklasse && <div><span className="aa-spec-key">DFK:</span> {specs.Druckfestigkeitsklasse}</div>}
        </td>
        <td className="aa-znumber">
          {result.z_number ||
            (result.match_basis === "specs" ? (
              <span className="ds-meta aa-via-specs">via specs</span>
            ) : ("—"))}
        </td>
        <td className="aa-zdoc">{result.z_document_name || "—"}</td>
        <td className="aa-status"><span className={statusBadgeClass(label)}>{statusText(label)}</span></td>
        <td className="aa-score">{scorePct(result.score)}</td>
        <td className="aa-page">{lv.page_number || "—"}</td>
      </tr>

      {expanded && (
        <tr className="aa-expand-row">
          <td colSpan={8} className="aa-expand-cell">
            {((result.matched_conditions || []).length > 0 || (result.unmatched_conditions || []).length > 0) && (
              <div className="aa-conditions">
                <h4 className="aa-expand-heading">Conditions</h4>
                <table className="ds-table aa-cond-table">
                  <thead>
                    <tr><th>Field</th><th>Z-doc requires</th><th>LV specifies</th><th /></tr>
                  </thead>
                  <tbody>
                    {[...(result.matched_conditions || []), ...(result.unmatched_conditions || [])].map((c, i) => (
                      <tr key={i} className={c.matched ? "aa-cond-match" : "aa-cond-fail"}>
                        <td>{c.field}</td>
                        <td>{c.z_value}</td>
                        <td>{c.lv_value || "—"}</td>
                        <td className="aa-cond-mark">{c.matched ? "✓" : "✗"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(result.raw_passages || []).length > 0 && (
              <div className="aa-passages">
                <h4 className="aa-expand-heading">Source passages</h4>
                {(result.raw_passages || []).map((p, i) => (
                  <div key={i} className="aa-passage-md"><ReactMarkdown {...MD_PROPS}>{p || ""}</ReactMarkdown></div>
                ))}
              </div>
            )}

            {docAssets === "loading" && <p className="ds-help aa-hint">Lädt Abbildungen &amp; strukturierte Daten…</p>}
            {docAssets === "error" && <p className="ds-help aa-hint">Abbildungen/Daten konnten nicht geladen werden.</p>}
            {docAssets && typeof docAssets === "object" && (
              <>
                {(docAssets.images || []).length > 0 && (
                  <div className="aa-figures">
                    <h4 className="aa-expand-heading">Abbildungen aus dem Z-Dokument</h4>
                    <div className="aa-figure-grid">
                      {docAssets.images.map((url, i) => (
                        <a key={i} href={`${API_BASE_URL}${url}`} target="_blank" rel="noreferrer" className="aa-figure">
                          <img src={`${API_BASE_URL}${url}`} alt={`Abbildung ${i + 1}`} loading="lazy" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {(docAssets.sections || []).length > 0 ? (
                  <div className="aa-structured">
                    <h4 className="aa-expand-heading">Strukturierte Daten</h4>
                    <ComplianceBrief
                      brief={{ document_name: docAssets.document_name, sections: docAssets.sections }}
                      documentId={result.z_document_id}
                      database={database}
                    />
                  </div>
                ) : (
                  <p className="ds-help aa-hint">Strukturierte Daten: Schema-Analyse für dieses Z-Dokument noch nicht ausgeführt.</p>
                )}
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
